import type { PortalEditorView, PortalEditEntry } from '../../../lib/api';
import { QUIET } from './editorShared';

/**
 * VP9 - the history of every change to a portal: each edit made in the app and each import, newest first - who, when,
 * what it was and what it became, and what happened to it since (undone, absorbed when the roster caught up, replaced
 * when an import was taken over it). Undo shows only where the server says a change may be undone: the latest change
 * to a thing.
 */

const when = (iso?: string) => (iso ? new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '');
const who = (by?: PortalEditEntry['by']) => by?.name ?? by?.email ?? by?.via ?? 'someone';
const rec = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' ? (v as Record<string, unknown>) : {});
const text = (v: unknown) => (v === null || v === undefined ? '' : String(v));

export const describeChange = (e: PortalEditEntry, view: PortalEditorView): string => {
  const dealName = (id: unknown) => view.deals.find((d) => d.id === id)?.name ?? text(id);
  switch (e.kind) {
    case 'status':
      return `Status - ${dealName(e.target)}`;
    case 'timelineAdd':
      return 'Timeline entry added';
    case 'openItemAdd':
      return 'Open item added';
    case 'openItemEdit':
      return 'Open item edited';
    case 'openItemClose':
      return 'Open item closed';
    case 'import':
      return `Import (${e.import?.path ?? 'roster'})`;
  }
};

/** One side of a change, in words. */
const side = (e: PortalEditEntry, v: unknown): string => {
  const o = rec(v);
  if (!Object.keys(o).length) return '';
  if (e.kind === 'status') return `${text(o.state)}: ${text(o.label)}${o.asOf ? ` (as of ${text(o.asOf)})` : ''}`;
  if (e.kind === 'timelineAdd') return `${text(o.date)} - ${text(o.title)}`;
  return `${text(o.item)}${o.closedAt ? ' (closed)' : ''}`;
};

const importSummary = (e: PortalEditEntry): string => {
  const i = e.import;
  if (!i) return '';
  const list = (xs: number[]) => xs.map((n) => `#${n}`).join(', ');
  // A change taken over in a clash is already said by its clash line - listing it twice read as two events.
  const clashed = new Set(i.clashes.map((c) => c.seq));
  const takenQuietly = i.superseded.filter((n) => !clashed.has(n));
  const parts = [
    i.rebased ? 'rebuilt from the stored page after a write-around' : '',
    ...(i.rebasedOver ?? []).map((o) => `the write-around touched #${o.seq} (${o.target}) - your change stood`),
    i.stamped.length ? `${i.stamped.length} deal(s) marked Updated` : '',
    i.kept.length ? `kept ${list(i.kept)}` : '',
    i.absorbed.length ? `absorbed ${list(i.absorbed)}` : '',
    takenQuietly.length ? `the roster taken over ${list(takenQuietly)}` : '',
    ...i.clashes.map((c) => `clash #${c.seq} (${c.target}): ${c.resolution === 'keep' ? 'your change kept' : 'the roster taken'}`),
  ].filter(Boolean);
  return parts.length ? parts.join('; ') : 'nothing in the app to merge';
};

/**
 * On a phone (below `md`, where the app's own sidebar folds away) the table stacks into one card per change: the number,
 * the time and the person first, the change under them, then Before / After / Now each under its own label - the
 * cell's `data-label`, drawn by CSS, so the words a test or a screen reader gets stay the table's. Empty cells fold away.
 * Found at 390 px on production (2026-09-11): the seven columns left the change, Before, After, Now and Undo off the
 * right edge, with tall blank bands and nothing saying to swipe.
 * ⚠️ The first three are INLINE-BLOCK, not inline: with no space between the cells, "1:21 PM" and "joseph@apex-res.com"
 * read as one word to the line breaker, which ran 3 px past the card before it could break at the hyphen.
 */
const PHONE_INLINE = 'max-md:inline-block max-md:p-0 max-md:pr-2';
const PHONE_BLOCK = 'max-md:block max-md:max-w-none max-md:p-0 max-md:pt-1 max-md:text-left max-md:empty:hidden';
const PHONE_LABELLED = `${PHONE_BLOCK} max-md:before:block max-md:before:content-[attr(data-label)] max-md:before:text-xs max-md:before:uppercase max-md:before:text-gray-500`;

const stateOf = (e: PortalEditEntry): string => {
  switch (e.state) {
    case 'active':
      return 'Live';
    case 'undone':
      return `Undone${e.stateBy ? ` by ${who(e.stateBy)}` : ''}${e.stateAt ? `, ${when(e.stateAt)}` : ''}`;
    case 'absorbed':
      return 'The roster now says the same';
    case 'superseded':
      return 'Replaced by an import';
    default:
      return '';
  }
};

export default function EditHistory({
  view,
  onUndo,
  errors,
  disabled,
  onReload,
}: {
  view: PortalEditorView;
  onUndo: (seq: number) => void;
  errors: string[];
  disabled: boolean;
  onReload: () => void;
}) {
  return (
    <div className="mt-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-2">
        Changes <span className="font-normal text-gray-500">({view.history.length})</span>
      </h3>
      {errors.length > 0 && (
        <div role="alert" className="mb-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          <ul className="space-y-1">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
          <button type="button" className={`${QUIET} mt-2`} onClick={onReload}>
            Reload
          </button>
        </div>
      )}
      {!view.history.length ? (
        <p className="text-sm text-gray-500">No changes made in the app yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm max-md:block" aria-label="Changes">
            <thead className="max-md:hidden">
              <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
                <th className="py-2 pr-3">#</th>
                <th className="py-2 pr-3">When</th>
                <th className="py-2 pr-3">Who</th>
                <th className="py-2 pr-3">Change</th>
                <th className="py-2 pr-3">Before</th>
                <th className="py-2 pr-3">After</th>
                <th className="py-2 pr-3">Now</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody className="max-md:block">
              {view.history.map((e) => (
                <tr key={e.id} className={`border-b border-gray-100 align-top max-md:block max-md:py-3 ${e.state === 'active' || e.state === 'record' ? '' : 'opacity-60'}`}>
                  <td className={`py-2 pr-3 text-gray-500 ${PHONE_INLINE}`}>#{e.seq}</td>
                  <td className={`py-2 pr-3 text-gray-500 whitespace-nowrap ${PHONE_INLINE}`}>{when(e.at)}</td>
                  <td className={`py-2 pr-3 text-gray-900 ${PHONE_INLINE}`}>{who(e.by)}</td>
                  <td className={`py-2 pr-3 text-gray-900 ${PHONE_BLOCK}`}>
                    {describeChange(e, view)}
                    {e.source && <div className="text-xs text-gray-500">Source (internal): {e.source}</div>}
                  </td>
                  <td data-label="Before" className={`py-2 pr-3 text-gray-600 max-w-[280px] ${PHONE_LABELLED}`}>{e.kind === 'import' ? '' : side(e, e.before)}</td>
                  <td data-label="After" className={`py-2 pr-3 text-gray-900 max-w-[280px] ${PHONE_LABELLED}`}>{e.kind === 'import' ? importSummary(e) : side(e, e.after)}</td>
                  <td data-label="Now" className={`py-2 pr-3 text-gray-500 ${PHONE_LABELLED}`}>{stateOf(e)}</td>
                  <td className={`py-2 text-right ${PHONE_BLOCK}`}>
                    {e.undoable && (
                      <button type="button" className={QUIET} disabled={disabled} onClick={() => onUndo(e.seq)}>
                        Undo #{e.seq}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
